import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { Injector, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@shared/services';
import { ForceAny } from '@shared/typescript';
import { Observable, catchError, from, of, switchMap, throwError } from 'rxjs';
import { CamelToNormalPipe } from '../../entities/pipes/camel-to-normal.pipe';
import { Router } from '@angular/router';
import { urlSafeBase64Encode } from '../../shared/utils';

/**
 * A utility function to create a readonly string literal type from a string key.
 * @param key - The input key.
 * @returns A readonly string literal type.
 */
const asConst = <TKey extends string>(key: TKey) => key;

/**
 * An object that maps HTTP error status codes to corresponding error keys.
 */
const errorCodeKeys = {
  403: asConst('forbiddenErr'), // Key for 403 status code
  401: asConst('unAuthErr'), // Key for 401 status code
  default: asConst('unknownErr'), // Default key for other status codes
};

/**
 * Get the base translation key based on the provided error key.
 * @param key - The error key for which the base translation key is needed.
 * @returns The base translation key.
 */
const getTranslationKeyOf = (key: keyof typeof errorCodeKeys) =>
  `messages.error.${errorCodeKeys[key] ?? 'unknownErr'}`; // Default to 'unknownErr' if key is not found

export const ErrorHandlerInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const injector = inject(Injector);
  const toastService = inject(ToastService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      // Check if the response is a Blob
      if (err.error instanceof Blob) {
        const reader = new FileReader();
        const obs = new Observable((observer) => {
          reader.onload = () => {
            try {
              const text = reader.result as string;
              const jsonError = JSON.parse(text);
              observer.next(jsonError);
              observer.complete();
            } catch (e) {
              observer.error(e);
            }
          };
          reader.onerror = (e) => {
            observer.error(e);
          };
          reader.readAsText(err.error);
        });

        return from(obs).pipe(
          switchMap((jsonError: ForceAny) => {
            handleError(jsonError, injector, toastService, router);
            return throwError(() => jsonError);
          }),
        );
      } else {
        handleError(err, injector, toastService, router);
        return throwError(() => err);
      }
    }),
  );
};

function handleError(
  err: ForceAny,
  injector: Injector,
  toastService: ToastService,
  router: Router,
) {
  try {
    const camelToNormal = new CamelToNormalPipe();
    const statusCode = err?.error?.status ?? err?.status ?? err?.statusCode;
    const translateService = injector.get(TranslateService);
    const validationError = Object.values(
      err?.error?.errors?.validationErrors?.[0]?.errors?.[0] ?? {},
    )?.[0];
    const priorityMessage =
      validationError ??
      err.error?.errors?.[0]?.error ??
      err.error?.message ??
      err?.message;

    const translationKey = getTranslationKeyOf(statusCode);

    const priorityMessages = camelToNormal.transform(priorityMessage);
    translateService
      .get(translationKey)
      .pipe(
        switchMap((message) => {
          const translationToBeShown =
            priorityMessages || (statusCode === 401 ? 'Unauthorized' : message);

          if (translationToBeShown === translationKey) {
            return translateService.get(getTranslationKeyOf('default'));
          } else {
            return of(translationToBeShown);
          }
        }),
      )
      .subscribe((message) => {
        if (statusCode === 503) {
          toastService.openToastAsText('Service Unavailable', '');
          return;
        }

        if (statusCode === 500) {
          toastService.openToastAsText(err?.message ?? 'Internal Server Error');
          return;
        }
        if (statusCode === 403 && !err?.error?.errorCode) {
          toastService.openToastAsText(
            'You do not have access to this feature. Please contact your administrator ',
          );
          return;
        }
        if (statusCode !== 401) {
          toastService.openToastAsText(priorityMessages || message, '');
        }

        if (statusCode === 408) {
          void router.navigateByUrl(
            `/auth/session-expired/${urlSafeBase64Encode(
              JSON.stringify({ redirectionFromFlydocs: true }),
            )}`,
          );
          return;
        }
      });
  } catch (error) {
    console.error('Error: Translation Text is not available', error, err); // Log both the caught error and the original error
  }
}
